<template>
  <div class="display-flex flex-col items-start w-full" style="gap: 15px;">
    <div class="w-full">
      <h2>Gerencie os canais de venda da empresa</h2>
      <p>Aqui você poderá alterar quais canais de venda esta empresa está integrada.</p>
      <p>Só poderá gerenciar quando essa empresa estiver integrada com o Ecommerce.</p>
    </div>

    <div v-if="loading" class="display-flex justify-center w-full">
      <Icon type="ios-loading" size="32" class="demo-spin-icon-load"/>
    </div>

    <div class="w-full" v-else>
      <span v-if="errorMessage" style="color: red; font-size: 14px;">
        {{ errorMessage }}
      </span>

      <div v-if="hasToRegisterFirst" class="display-flex flex-col items-start w-full" style="gap: 15px;">
        <span style="font-size: 14px;" class="text-warning">
          Primeiro registre os canais de venda que a empresa quer integrar:
        </span>

        <ul>
          <li v-for="channel in wantedSaleChannelsNames" :key="channel">
            {{ channel }}
          </li>
        </ul>

        <i-button type="primary" @click="register" :loading="saving" :disabled="saving">
          Registrar canais de venda
        </i-button>

        <span v-if="saveErrorMessage" style="font-size: 14px;" class="text-success">
          {{ saveErrorMessage }}
        </span>

        <span v-if="saveSuccessMessage" style="font-size: 14px;" class="text-danger">
          {{ saveSuccessMessage }}
        </span>
      </div>


      <div v-if="!errorMessage && !hasToRegisterFirst" class="display-flex flex-col items-start w-full" style="gap: 15px;">
        <div class="display-flex justify-between w-full mb-3">
          <span>Selecione os canais de venda que deseja integrar</span>

          <div class="display-flex justify-end" style="gap: 10px;">
            <i-button type="primary" @click="restoreFromBackup" :disabled="!selectedSaleChannelsIsDifferent || saving">
              Restaurar seleção
            </i-button>

            <i-button type="primary" @click="save" :disabled="!selectedSaleChannelsIsDifferent || saving" :loading="saving">
              Salvar
            </i-button>
          </div>
        </div>

        <span v-if="saveErrorMessage" style="font-size: 14px;" class="text-danger">
          {{ saveErrorMessage }}
        </span>

        <span v-if="saveSuccessMessage" style="font-size: 14px;" class="text-success">
          {{ saveSuccessMessage }}
        </span>

        <!-- Campo de busca -->
        <div class="w-full mb-3">
          <i-input
            v-model="searchTerm"
            placeholder="Buscar canal de venda..."
            clearable
            size="large"
            style="max-width: 400px;"
          />
        </div>

        <div class="display-flex flex-wrap w-full" style="gap: 10px;">
          <vx-checkbox
            v-for="item in filteredAndSortedSaleChannels"
            :key="item.id"
            :label="item.name"
            :text="''"
            :vs-value="item.name"
            :value="selectedSaleChannels.includes(item.id) ? item.name : null"
            name="sale_channels"
            v-validate="'required'"
            @input="toggleSaleChannel(item.id, $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GerenciarCanaisDeVenda",
  props: {
    id_empresa: Number
  },
  data() {
    return {
      loading: false,
      allSaleChannels: [],
      selectedSaleChannels: [], // Array de IDs
      backupSelectedSaleChannels: [], // Array de IDs
      errorMessage: null,
      saving: false,
      saveErrorMessage: null,
      saveSuccessMessage: null,
      searchTerm: '', // Campo de busca
      hasToRegisterFirst: false,
      wantedSaleChannels: []
    };
  },
  methods: {
    async getData() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get(`clientes/ecommerce/store/${this.$props.id_empresa}/sale-channels`);

        if (data.codigo === 0) {
          throw new Error(data.message || "Erro ao carregar os canais de venda.");
        }

        this.allSaleChannels = data.all_sale_channels;

        if (data.has_to_register_first) {
          this.hasToRegisterFirst = true;
          this.wantedSaleChannels = data.wanted_sale_channels || [];
          this.loading = false;
          return;
        }

        this.selectedSaleChannels = data.sale_channels.map(channel => channel.id);
        this.backupSelectedSaleChannels = [...this.selectedSaleChannels];
      } catch (error) {
        console.warn(error);
        this.errorMessage = error.message;
      }
      this.loading = false;
    },

    restoreFromBackup() {
      this.selectedSaleChannels = [...this.backupSelectedSaleChannels];

      this.saveSuccessMessage = null;
      this.saveErrorMessage = null;
    },

    toggleSaleChannel(channelId, payload) {
      if (payload) {
        if (!this.selectedSaleChannels.includes(channelId)) {
          this.selectedSaleChannels.push(channelId);
        }
      } else {
        this.selectedSaleChannels = this.selectedSaleChannels
          .filter(id => id !== channelId);
      }
    },

    onChange() {
      this.selectedSaleChannels = this.selectedSaleChannels
        .filter(id => id != null);
    },

    register() {
      this.saving = true;
      this.saveSuccessMessage = null;
      this.saveErrorMessage = null;

      this.$axios.post(
        `clientes/ecommerce/store/${this.id_empresa}/sale-channels`
      )
        .then(({ data }) => {
          this.saving = false;

          if (data.codigo === 1) {
            this.saveSuccessMessage = "Canais de venda registrados com sucesso!";
            this.selectedSaleChannels = data.sale_channels.map(channel => channel.id);
            this.backupSelectedSaleChannels = [...this.selectedSaleChannels];
            this.hasToRegisterFirst = false;
          } else {
            throw new Error(data.message || "Erro ao registrar os canais de venda.");
          }
        })
        .catch(err => {
          this.saving = false;
          this.saveErrorMessage = err.message || "Erro ao registrar os canais de venda.";
        });
    },

    save() {
      this.saving = true;
      this.saveSuccessMessage = null;
      this.saveErrorMessage = null;

      this.$axios.put(
        `clientes/ecommerce/store/${this.id_empresa}/sale-channels`,
        { sale_channels: this.selectedSaleChannels }
      )
        .then(({ data }) => {
          this.saving = false;

          if (data.codigo === 1) {
            this.saveSuccessMessage = "Canais de venda salvos com sucesso!";
            this.selectedSaleChannels = data.sale_channels.map(channel => channel.id);
            this.backupSelectedSaleChannels = [...this.selectedSaleChannels];
          } else {
            throw new Error(data.message || "Erro ao salvar os canais de venda.");
          }
        })
        .catch(err => {
          this.saving = false;
          this.saveErrorMessage = err.message || "Erro ao salvar os canais de venda.";
        });
    },

    // Função para normalizar texto (remove acentos e converte para minúsculo)
    normalizeText(text) {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // Remove acentos
    }
  },
  computed: {
    // Ordena e filtra os canais dinamicamente
    filteredAndSortedSaleChannels() {
      let channels = [...this.allSaleChannels];

      // Aplica filtro de busca se houver termo
      if (this.searchTerm.trim()) {
        const normalizedSearch = this.normalizeText(this.searchTerm.trim());
        channels = channels.filter(channel =>
          this.normalizeText(channel.name).includes(normalizedSearch)
        );
      }

      // Ordena: selecionados primeiro, depois não selecionados
      // Dentro de cada grupo, ordena alfabeticamente
      return channels.sort((a, b) => {
        const aSelected = this.selectedSaleChannels.includes(a.id);
        const bSelected = this.selectedSaleChannels.includes(b.id);

        // Se um está selecionado e outro não, o selecionado vem primeiro
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        // Se ambos têm o mesmo status (ambos selecionados ou ambos não selecionados),
        // ordena alfabeticamente
        return a.name.localeCompare(b.name);
      });
    },

    selectedSaleChannelsIsDifferent() {
      return JSON.stringify([...this.selectedSaleChannels].sort()) !==
        JSON.stringify([...this.backupSelectedSaleChannels].sort());
    },

    wantedSaleChannelsNames() {
      return this.allSaleChannels.filter(channel =>
        this.wantedSaleChannels.includes(channel.id)
      ).map(channel => channel.name);
    }
  },
  created() {
    this.getData();
  }
};
</script>

<style scoped>
.demo-spin-icon-load {
  animation: ani-demo-spin 1s linear infinite;
}

@keyframes ani-demo-spin {
  from {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(180deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
